import { Component, inject, Input, ViewChild } from '@angular/core'
import { Call, CallResource, CallsService, MinutesStyling, MetricType } from '../calls.service'
import { MeetingDetailsCardComponent, MeetingDetailsSaveData } from '../meeting-details-card/meeting-details-card.component'
import { MatIcon } from '@angular/material/icon'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import { AsyncPipe, DatePipe } from '@angular/common'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatTooltip } from '@angular/material/tooltip'
import { CallDocumentUploadComponent, getMissingDocumentsForCall } from '../call-document-upload/call-document-upload.component'
import { UsersService } from '../users.service'
import { ToastService } from '../toast.service'
import { DownloadsService } from '../downloads.service'
import { FormsModule } from '@angular/forms'
import { SelectComponent, SelectOption } from '../common/select/select.component'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MetricsService } from '../metrics.service'
import { CallProfilesService } from '../call-profiles.service'
import { filter, map, switchMap } from 'rxjs'
import { MatButton } from '@angular/material/button'

@Component({
  selector: 'app-call-info',
  standalone: true,
  imports: [
    MatIcon,
    MinutesStylingComponent,
    DatePipe,
    TranslateModule,
    MatTooltip,
    CallDocumentUploadComponent,
    MatButton,
    MeetingDetailsCardComponent,
    AsyncPipe,
    FormsModule,
    SelectComponent,
  ],
  templateUrl: './call-info.component.html',
  styleUrl: './call-info.component.scss',
})
export class CallInfoComponent {
  @Input()
  call!: Call

  @Input()
  canEdit!: boolean

  @ViewChild(MeetingDetailsCardComponent)
  meetingDetailsCard: MeetingDetailsCardComponent | undefined

  constructor() {}

  resetEditState() {
    if (this.meetingDetailsCard !== undefined) {
      this.meetingDetailsCard.cancelEditDetails()
    }
  }

  callsService = inject(CallsService)
  usersService = inject(UsersService)
  currentUser$ = this.usersService.currentUser$
  toastService = inject(ToastService)
  downloadsService = inject(DownloadsService)
  translateService = inject(TranslateService)
  metricsService = inject(MetricsService)
  callProfilesService = inject(CallProfilesService)
  dialog = inject(MatDialog)

  updateCallProfileMutation = this.callsService.updateCallProfileMutation()
  callProfiles$ = this.currentUser$.pipe(
    filter((result) => result !== null),
    map((result) => result.organizationId),
    switchMap((orgId) => this.callProfilesService.getCallProfilesByOrganizationId(orgId))
  )

  patchCallMutation = this.callsService.patchMutation()
  updateMetadataMutation = this.callsService.updateMetadataMutation()
  removeResourceMutation = this.callsService.removeResource()
  reprocessOutputsMutation = this.callsService.reprocessOutputsMutation()

  async onDetailsSaved(saveData: MeetingDetailsSaveData): Promise<void> {
    await this.updateMetadataMutation.mutateAsync({
      callId: this.call.id,
      data: saveData.metadata,
    })
    if (saveData.dateTime !== null) {
      await this.patchCallMutation.mutateAsync({
        callId: this.call.id,
        data: {
          date: saveData.dateTime.date,
          endDate: saveData.dateTime.endDate,
        },
      })
    }
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  getCallProfileOptions(callProfiles: { id: number; name: string }[]): SelectOption<number>[] {
    return callProfiles.map((profile) => ({ value: profile.id, label: profile.name }))
  }

  confirmCallProfileChange = (_callProfileId: number | null): Promise<boolean> => {
    if (!this.call.isPostProcessingComplete) {
      return Promise.resolve(true)
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.CALL_INFO.CHANGE_CALL_PROFILE_DIALOG.TITLE'),
        description: this.translateService.instant('CALLS.CALL_INFO.CHANGE_CALL_PROFILE_DIALOG.DESCRIPTION'),
      },
    })
    return new Promise((resolve) => {
      dialogRef.afterClosed().subscribe((result) => resolve(!!result))
    })
  }

  async updateCallProfile(callProfileId: number | null) {
    await this.updateCallProfileMutation.mutateAsync({ callId: this.call.id, data: { callProfileId } })
    this.toastService.show('CALLS.UPDATED')
  }

  async removeUpload(resource: CallResource) {
    await this.removeResourceMutation.mutateAsync({
      callId: this.call.id,
      resourceType: resource.resourceType,
      index: resource.index,
      extension: resource.extension,
    })
    this.toastService.show('CALLS.CALL_INFO.DOCUMENT_UPLOAD.REMOVE_SUCCESS')
  }

  downloadResource(resource: CallResource) {
    if (resource.downloadUrl !== null) {
      const fileName = `${resource.fileName}.${resource.extension}`
      this.downloadsService.downloadFromUrl(resource.downloadUrl, fileName)
    }
  }

  get missingDocuments() {
    const missingDocuments = getMissingDocumentsForCall(this.call)
    if (this.call.agendaType === 'MULTIPLE') {
      return missingDocuments.filter((doc) => doc.type !== 'AGENDA')
    }
    return missingDocuments
  }

  async updateMinutesStyling(styling: Partial<MinutesStyling>) {
    await this.patchCallMutation.mutateAsync({ callId: this.call.id, data: styling })
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
    await this.metricsService.sendMetric(this.call.id, MetricType.CHANGE_MINUTING_STYLES, styling)
  }

  get isAuthorizedToManageDocuments() {
    return this.call.accessLevel === 'FULL_ACCESS' || this.call.accessLevel === 'EDITOR'
  }

  get anyAttendeeMissingHonorific(): boolean {
    return this.call.agendaMetadata.attendees.some(
      (attendee) => attendee.honorifics === null || attendee.honorifics === undefined || attendee.honorifics.trim() === ''
    )
  }

  reprocessOutputs() {
    const titleKey = this.call.hasMinutes ? 'CALLS.REPROCESS_OUTPUTS.TITLE' : 'CALLS.REPROCESS_OUTPUTS.TITLE_FIRST_TIME'
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(titleKey),
        description: this.translateService.instant('CALLS.REPROCESS_OUTPUTS.DESCRIPTION', { callId: this.call.id }),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.reprocessOutputsMutation.mutateAsync({ callId: this.call.id })
      }
    })
  }
}
